/** Recognized MIME types. */
export interface IMimeType {
  /** File MIME type (contentType). */
  mime: string;
  /** Extensions matching the file type. */
  extension: string[];
  /** Label to show for file type. */
  label?: string;
  /** Further descriptive information.. */
  description?: string;
}

/** Default for unknown types. */
const DEFAULT_BINARY: IMimeType = {
  mime: 'application/octet-stream',
  extension: ['.bin'],
  description: 'Any kind of binary data'
};

/**
 * Common MIME types, see https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types.
 */
const MIME_TYPES: IMimeType[] = [
  { mime: 'audio/aac', extension: ['.aac'], description: 'AAC audio' },
  {
    mime: 'audio/x-aiff',
    extension: ['.aiff'],
    description: 'AIFF: Audio Interchange File Format'
  },
  {
    mime: 'audio/amr',
    extension: ['.amr'],
    description: 'AMR: Adaptive Multi-Rate'
  },
  {
    mime: 'application/x-abiword',
    extension: ['.abw'],
    description: 'AbiWord document'
  },
  {
    mime: 'application/x-freearc',
    extension: ['.arc'],
    description: 'Archive document (multiple files embedded)'
  },
  {
    mime: 'audio/basic',
    extension: ['.au'],
    description: 'Audio file'
  },
  {
    mime: 'video/x-msvideo',
    extension: ['.avi'],
    description: 'AVI: Audio Video Interleave'
  },
  {
    mime: 'video/x-ms-asf',
    extension: ['.asx'],
    description: 'ASX: Advanced Stream Redirector'
  },
  {
    mime: 'application/vnd.amazon.ebook',
    extension: ['.azw'],
    description: 'Amazon Kindle eBook format'
  },
  {
    mime: 'application/octet-stream',
    extension: ['.bin'],
    description: 'Any kind of binary data'
  },
  {
    mime: 'image/bmp',
    extension: ['.bmp'],
    description: 'Windows OS/2 Bitmap Graphics'
  },
  {
    mime: 'application/x-bzip',
    extension: ['.bz'],
    description: 'BZip archive'
  },
  {
    mime: 'application/x-bzip2',
    extension: ['.bz2'],
    description: 'BZip2 archive'
  },
  { mime: 'application/x-cdf', extension: ['.cda'], description: 'CD audio' },
  {
    mime: 'application/x-csh',
    extension: ['.csh'],
    description: 'C-Shell script'
  },
  {
    mime: 'text/css',
    extension: ['.css'],
    description: 'Cascading Style Sheets (CSS)'
  },
  {
    mime: 'text/csv',
    extension: ['.csv'],
    description: 'Comma-separated values (CSV)'
  },
  {
    mime: 'application/msword',
    extension: ['.doc', '.dot'],
    description: 'Microsoft Word',
    label: 'Word'
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: ['.docx'],
    description: 'Microsoft Word (OpenXML)',
    label: 'Word'
  },
  {
    mime: 'application/vnd.ms-fontobject',
    extension: ['.eot'],
    description: 'MS Embedded OpenType fonts'
  },
  {
    mime: 'application/epub+zip',
    extension: ['.epub'],
    description: 'Electronic publication (EPUB)',
    label: 'EPub'
  },
  {
    mime: 'audio/x-flac',
    extension: ['.flac'],
    description: 'FLAC: Free Lossless Audio Codec'
  },
  {
    mime: 'application/gzip',
    extension: ['.gz'],
    description: 'GZip Compressed Archive'
  },
  {
    mime: 'image/gif',
    extension: ['.gif'],
    description: 'Graphics Interchange Format (GIF)'
  },
  {
    mime: 'text/html',
    extension: ['.html', '.htm'],
    description: '.html	HyperText Markup Language (HTML)'
  },
  {
    mime: 'image/vnd.microsoft.icon',
    extension: ['.ico'],
    description: 'Icon format'
  },
  {
    mime: 'text/calendar',
    extension: ['.ics'],
    description: 'iCalendar format'
  },
  {
    mime: 'application/java-archive',
    extension: ['.jar'],
    description: 'Java Archive (JAR)'
  },
  {
    mime: 'image/jpeg',
    extension: ['.jpg', '.jpeg'],
    description: '.jpg	JPEG images'
  },
  {
    mime: 'text/javascript',
    extension: ['.js'],
    description: 'JavaScript (Specifications: HTML and its reasoning, and IETF)'
  },
  {
    mime: 'application/json',
    extension: ['.json'],
    description: 'JSON format',
    label: 'JSON'
  },
  {
    mime: 'application/ld+json',
    extension: ['.jsonld'],
    description: 'JSON-LD format'
  },
  {
    mime: 'audio/midi audio/x-midi',
    extension: ['.mid'],
    description: '.midi	Musical Instrument Digital Interface (MIDI)'
  },
  {
    mime: 'text/javascript',
    extension: ['.mjs'],
    description: 'JavaScript module'
  },
  { mime: 'audio/mpeg', extension: ['.mp3'], description: 'MP3 audio' },
  { mime: 'audio/mp4a-latm', extension: ['.m4a'], description: 'MPEG4 audio' },
  { mime: 'video/mp4', extension: ['.mp4'], description: 'MP4 video' },
  {
    mime: 'video/mpeg',
    extension: ['.mpeg', '.mpg'],
    description: 'MPEG Video'
  },
  { mime: 'video/x-m4v', extension: ['.m4v'], description: 'MPEG4 Video' },
  { mime: 'video/quicktime', extension: ['.mov'], description: 'MOV Video' },

  {
    mime: 'application/vnd.apple.installer+xml',
    extension: ['.mpkg'],
    description: 'Apple Installer Package'
  },
  {
    mime: 'application/vnd.oasis.opendocument.presentation',
    extension: ['.odp'],
    description: 'OpenDocument presentation document'
  },
  {
    mime: 'application/vnd.oasis.opendocument.spreadsheet',
    extension: ['.ods'],
    description: 'OpenDocument spreadsheet document'
  },
  {
    mime: 'application/vnd.oasis.opendocument.text',
    extension: ['.odt'],
    description: 'OpenDocument text document'
  },
  { mime: 'audio/ogg', extension: ['.oga'], description: 'OGG audio' },
  { mime: 'video/ogg', extension: ['.ogv', '.ogm'], description: 'OGG video' },
  { mime: 'application/ogg', extension: ['.ogx'], description: 'OGG' },
  { mime: 'audio/opus', extension: ['.opus'], description: 'Opus audio' },
  { mime: 'font/otf', extension: ['.otf'], description: 'OpenType font' },
  {
    mime: 'image/png',
    extension: ['.png'],
    description: 'Portable Network Graphics'
  },
  {
    mime: 'application/pdf',
    extension: ['.pdf'],
    description: 'Adobe Portable Document Format (PDF)',
    label: 'PDF'
  },
  {
    mime: 'application/x-httpd-php',
    extension: ['.php'],
    description: 'Hypertext Preprocessor (Personal Home Page)'
  },
  {
    mime: 'application/vnd.ms-powerpoint',
    extension: ['.ppt', '.pot', '.pps', '.ppa'],
    description: 'Microsoft PowerPoint'
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: ['.pptx'],
    description: 'Microsoft PowerPoint (OpenXML)'
  },
  {
    mime: 'application/vnd.rar',
    extension: ['.rar'],
    description: 'RAR archive'
  },
  {
    mime: 'application/rtf',
    extension: ['.rtf'],
    description: 'Rich Text Format (RTF)'
  },
  {
    mime: 'application/x-sh',
    extension: ['.sh'],
    description: 'Bourne shell script'
  },
  {
    mime: 'image/svg+xml',
    extension: ['.svg'],
    description: 'Scalable Vector Graphics (SVG)'
  },
  {
    mime: 'application/x-shockwave-flash',
    extension: ['.swf'],
    description: 'Small web format (SWF) or Adobe Flash document'
  },
  {
    mime: 'application/x-tar',
    extension: ['.tar'],
    description: 'Tape Archive (TAR)'
  },
  {
    mime: 'image/tiff',
    extension: ['.tif'],
    description: '.tiff	Tagged Image File Format (TIFF)'
  },
  {
    mime: 'video/mp2t',
    extension: ['.ts'],
    description: 'MPEG transport stream'
  },
  { mime: 'font/ttf', extension: ['.ttf'], description: 'TrueType Font' },
  {
    mime: 'text/plain',
    extension: ['.txt'],
    description: 'Text, (generally ASCII or ISO 8859-n)',
    label: 'Text'
  },
  {
    mime: 'application/vnd.visio',
    extension: ['.vsd'],
    description: 'Microsoft Visio'
  },
  {
    mime: 'audio/wav',
    extension: ['.wav'],
    description: 'Waveform Audio Format'
  },
  {
    mime: 'audio/webm',
    extension: ['.weba'],
    description: 'WEBM audio'
  },
  { mime: 'video/webm', extension: ['.webm'], description: 'WEBM video' },
  { mime: 'image/webp', extension: ['.webp'], description: 'WEBP image' },
  {
    mime: 'video/x-ms-wmv',
    extension: ['.wmv'],
    description: 'Windows Media Video'
  },
  {
    mime: 'audio/x-ms-wma',
    extension: ['.wma'],
    description: 'Windows Media Audio'
  },
  {
    mime: 'font/woff',
    extension: ['.woff'],
    description: 'Web Open Font Format (WOFF)'
  },
  {
    mime: 'font/woff2',
    extension: ['.woff2'],
    description: 'Web Open Font Format (WOFF)'
  },
  {
    mime: 'application/xhtml+xml',
    extension: ['.xhtml'],
    description: 'XHTML'
  },
  {
    mime: 'application/vnd.ms-excel',
    extension: ['.xls', '.xlt', '.xla'],
    description: 'Microsoft Excel',
    label: 'Excel'
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: ['.xlsx'],
    description: 'Microsoft Excel (OpenXML)',
    label: 'Excel'
  },
  {
    mime: 'application/xml',
    extension: ['.xml'],
    description:
      'XML. You can assign a specific MIME type to a file with .xml extension depending on how its contents are meant to be interpreted. For instance, an Atom feed is application/atom+xml'
  },
  {
    mime: 'text/xml',
    extension: ['.xml'],
    description:
      'application/xml is recommended as of RFC 7303 (section 4.1), but text/xml is still used sometimes.'
  },
  {
    mime: 'application/vnd.mozilla.xul+xml',
    extension: ['.xul'],
    description: 'XUL'
  },
  {
    mime: 'application/zip',
    extension: ['.zip'],
    description: 'ZIP archive',
    label: 'ZIP'
  },
  {
    mime: 'video/3gpp',
    extension: ['.3gp'],
    description:
      "3GPP audio/video container; audio/3gpp if it doesn't contain video"
  },
  {
    mime: 'video/3gpp2',
    extension: ['.3g2'],
    description:
      "3GPP2 audio/video container; audio/3gpp2 if it doesn't contain video"
  },
  {
    mime: 'application/x-7z-compressed',
    extension: ['.7z'],
    description: '7-zip archive'
  },
  {
    mime: 'application/vnd.ms-access',
    description: 'Micrisoft Access Database',
    extension: ['mdb']
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    description: 'Microsoft Office Word Template',
    extension: ['dotx'],
    label: 'Word'
  },
  {
    mime: 'application/vnd.ms-word.document.macroEnabled.12',
    description: 'Microsoft Office Word',
    extension: ['docm'],
    label: 'Word'
  },
  {
    mime: 'application/vnd.ms-word.template.macroEnabled.12',
    description: 'Microsoft Office Word',
    extension: ['dotm'],
    label: 'Word'
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    description: 'Microsoft Office Excel Template',
    extension: ['xltx'],
    label: 'Excel'
  },
  {
    mime: 'application/vnd.ms-excel.sheet.macroEnabled.12',
    description: 'Microsoft Office Excel',
    extension: ['xlsm'],
    label: 'Excel'
  },
  {
    mime: 'application/vnd.ms-excel.template.macroEnabled.12',
    description: 'Microsoft Office Excel',
    extension: ['xltm'],
    label: 'Excel'
  },
  {
    mime: 'application/vnd.ms-excel.addin.macroEnabled.12',
    description: 'Microsoft Office Excel',
    extension: ['xlam'],
    label: 'Excel'
  },
  {
    mime: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    description: 'Microsoft Office Excel',
    extension: ['xlsb'],
    label: 'Excel'
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.template',
    description: 'Microsoft Office Powerpoint Template',
    extension: ['potx'],
    label: 'PowerPoint'
  },
  {
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    description: 'Microsoft Office Powerpoint Slideshow',
    extension: ['ppsx'],
    label: 'PowerPoint'
  },
  {
    mime: 'application/vnd.ms-powerpoint.addin.macroEnabled.12',
    description: 'Microsoft Office Powerpoint',
    extension: ['ppam'],
    label: 'PowerPoint'
  },
  {
    mime: 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    description: 'Microsoft Office Powerpoint',
    extension: ['pptm'],
    label: 'PowerPoint'
  },
  {
    mime: 'application/vnd.ms-powerpoint.template.macroEnabled.12',
    description: 'Microsoft Office Powerpoint',
    extension: ['potm'],
    label: 'PowerPoint'
  },
  {
    mime: 'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
    description: 'Microsoft Office Powerpoint',
    extension: ['ppsm'],
    label: 'PowerPoint'
  },

  {
    mime: 'application/x-iwork-keynote-sffkey',
    description: 'Apple iWorks Keynote',
    extension: ['.keynote'],
    label: 'Keynote'
  },
  {
    mime: 'application/x-iwork-pages-sffpages',
    description: 'Apple iWorks Pages',
    extension: ['.pages'],
    label: 'Pages'
  },
  {
    mime: 'application/x-iwork-numbers-sffnumbers',
    description: 'Apple iWorks Numbers',
    extension: ['.numbers'],
    label: 'Numbers'
  },
  {
    mime: 'application/vnd.apple.pages',
    description: 'Apple iWorks Pages',
    extension: ['pages'],
    label: 'Pages'
  },
  {
    mime: 'application/vnd.apple.keynote',
    description: 'Apple iWorks Keynote',
    extension: ['.keynote'],
    label: 'Keynote'
  },
  {
    mime: 'application/vnd.apple.numbers',
    description: 'Apple iWorks Numbers',
    extension: ['.numbers'],
    label: 'Numbers'
  }
];
// WAS: MIME_TYPES = {
//   '.doc': 'application/msword', // Microsoft Word
// }

/** These document MIME Types are used in AssetUploader as allowed file types for learner to upload a file  */
export const documentFiles = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  'application/vnd.ms-access',
  'application/vnd.apple.numbers',
  'application/x-iwork-numbers-sffnumbers',
  'application/x-iwork-pages-sffpages',
  'application/vnd.apple.pages',
  'application/x-iwork-keynote-sffkey',
  'application/vnd.apple.keynote',
  'application/zip',
  'application/x-7z-compressed'
];

/** Regular expression matching extensions of the form /(.jpg|.png|...)$/i. */
const reExtensions = new RegExp(
  `(${MIME_TYPES.map((m) => m.extension.join('|'))
    .join('|')
    .replace(/\./g, '\\.')})$`,
  'i'
);

/**
 * Determine the MIME type from a file's name.
 * @param name Full name of file including the extension.
 */
const getMimeType = (name: string): string => {
  const match = reExtensions.exec(name);
  const mime =
    (match && MIME_TYPES.find((m) => m.extension.includes(match[1]))) ||
    DEFAULT_BINARY;
  return mime.mime;
};

/**
 * Returns MIME information based on content type, or `undefined` if no match is
 * found.
 * @param mime MIME type to search for.
 */
export const fromMime = (mime: string): IMimeType =>
  MIME_TYPES.find((m) => m.mime === mime);

export default getMimeType;
